import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { BookOpen, Loader2, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { db } from '../../firebase/config';
import { createSubjectId, getConfiguredSubjects, getDefaultSubjects } from '../../utils/semesterConfig';

const blankSubject = {
  id: '',
  code: '',
  name: '',
  iatMax: 20,
  iat1Max: 20,
  iat2Max: 20,
  eseMax: 60,
  teachingDivision: '',
  courseInchargeEmail: '',
  courseInchargeName: ''
};

const numberFields = new Set(['iatMax', 'iat1Max', 'iat2Max', 'eseMax']);

const getFacultyKey = (faculty) => faculty.email || faculty.id || faculty.username || faculty.loginId || '';

const getFacultyName = (faculty) => (
  faculty.name || faculty.displayName || faculty.email || faculty.username || faculty.id || ''
);

const facultyRolesToIgnore = new Set(['hod', 'administrator', 'admin', 'student']);

const SemesterSubjectsWidget = () => {
  const [semester, setSemester] = useState('1');
  const [subjectsBySemester, setSubjectsBySemester] = useState({ 1: [], 2: [] });
  const [faculty, setFaculty] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const subjects = useMemo(() => subjectsBySemester[semester] || [], [semester, subjectsBySemester]);

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const [sem1Doc, sem2Doc, usersSnap, studentsSnap, divisionSnap] = await Promise.all([
        getDoc(doc(db, 'semesterSubjects', 'semester1')),
        getDoc(doc(db, 'semesterSubjects', 'semester2')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'divisionAssignments'))
      ]);

      setSubjectsBySemester({
        1: getConfiguredSubjects(sem1Doc.exists() ? sem1Doc.data() : null, 1),
        2: getConfiguredSubjects(sem2Doc.exists() ? sem2Doc.data() : null, 2)
      });
      setFaculty(usersSnap.docs
        .map((userDoc) => ({ id: userDoc.id, ...userDoc.data() }))
        .filter((user) => !facultyRolesToIgnore.has(user.role))
        .sort((a, b) => getFacultyName(a).localeCompare(getFacultyName(b))));

      const divisionSet = new Set();
      studentsSnap.docs.forEach((studentDoc) => {
        const student = studentDoc.data();
        if (student.division) divisionSet.add(student.division);
      });
      divisionSnap.docs.forEach((divisionDoc) => {
        const division = divisionDoc.data();
        if (division.division) divisionSet.add(division.division);
        if (division.name) divisionSet.add(division.name);
        if (divisionDoc.id) divisionSet.add(divisionDoc.id);
      });
      setDivisions([...divisionSet].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
    } catch (loadError) {
      console.error('Error loading semester subjects:', loadError);
      setError('Could not load semester subjects.');
    } finally {
      setLoading(false);
    }
  };

  const updateSubject = (index, field, value) => {
    setSubjectsBySemester((current) => ({
      ...current,
      [semester]: current[semester].map((subject, subjectIndex) => (
        subjectIndex === index
          ? { ...subject, [field]: numberFields.has(field) ? value.replace(/\D/g, '') : value }
          : subject
      ))
    }));
  };

  const updateCourseIncharge = (index, email) => {
    const selectedFaculty = faculty.find((item) => getFacultyKey(item) === email);

    setSubjectsBySemester((current) => ({
      ...current,
      [semester]: current[semester].map((subject, subjectIndex) => (
        subjectIndex === index
          ? {
              ...subject,
              courseInchargeEmail: email,
              courseInchargeName: selectedFaculty ? getFacultyName(selectedFaculty) : ''
            }
          : subject
      ))
    }));
  };

  const addSubject = () => {
    setSubjectsBySemester((current) => ({
      ...current,
      [semester]: [
        ...current[semester],
        { ...blankSubject, id: `subject${Date.now()}` }
      ]
    }));
  };

  const removeSubject = (index) => {
    setSubjectsBySemester((current) => ({
      ...current,
      [semester]: current[semester].filter((_, subjectIndex) => subjectIndex !== index)
    }));
  };

  const resetDefaults = () => {
    setSubjectsBySemester((current) => ({
      ...current,
      [semester]: getDefaultSubjects(Number(semester))
    }));
  };

  const saveSubjects = async () => {
    const cleanedSubjects = subjects
      .map((subject) => ({
        id: subject.id || createSubjectId(subject),
        code: subject.code.trim().toUpperCase(),
        name: subject.name.trim(),
        iatMax: Number(subject.iat1Max || subject.iatMax || 20) || 20,
        iat1Max: Number(subject.iat1Max || subject.iatMax || 20) || 20,
        iat2Max: Number(subject.iat2Max || subject.iatMax || 20) || 20,
        eseMax: Number(subject.eseMax || 60) || 60,
        teachingDivision: subject.teachingDivision || '',
        courseInchargeEmail: subject.courseInchargeEmail || '',
        courseInchargeName: subject.courseInchargeName || ''
      }))
      .filter((subject) => subject.code || subject.name);

    if (cleanedSubjects.length === 0) {
      setError('Add at least one subject.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      await setDoc(doc(db, 'semesterSubjects', `semester${semester}`), {
        semester: Number(semester),
        subjects: cleanedSubjects,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setSubjectsBySemester((current) => ({
        ...current,
        [semester]: cleanedSubjects
      }));
      setMessage(`Semester ${semester} subjects saved.`);
    } catch (saveError) {
      console.error('Error saving semester subjects:', saveError);
      setError('Could not save semester subjects.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[220px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading subjects
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <label className="block md:w-60">
          <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Semester</span>
          <select
            value={semester}
            onChange={(event) => {
              setSemester(event.target.value);
              setMessage('');
              setError('');
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="1">Semester 1</option>
            <option value="2">Semester 2</option>
          </select>
        </label>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={loadSubjects}
            className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload
          </button>
          <button
            type="button"
            onClick={resetDefaults}
            className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Defaults
          </button>
          <button
            type="button"
            onClick={addSubject}
            className="inline-flex items-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Subject
          </button>
          <button
            type="button"
            onClick={saveSubjects}
            disabled={saving}
            className="inline-flex items-center rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:bg-gray-400"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <div className="flex items-center font-semibold">
          <BookOpen className="mr-2 h-4 w-4" />
          These subjects drive the End of Semester Report and course-incharge marks entry.
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-[1180px] w-full border-collapse text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Code</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Subject Name</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">IAT-1 Max</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">IAT-2 Max</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">ESE Max</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Division</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Course Incharge</th>
              <th className="border-b border-gray-200 px-3 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((subject, index) => (
              <tr key={subject.id || index} className="bg-white">
                <td className="border-r border-t border-gray-200 px-2 py-1">
                  <input
                    value={subject.code}
                    onChange={(event) => updateSubject(index, 'code', event.target.value)}
                    placeholder="AM-I"
                    className="w-full rounded border border-transparent px-2 py-1 font-semibold uppercase focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </td>
                <td className="border-r border-t border-gray-200 px-2 py-1">
                  <input
                    value={subject.name}
                    onChange={(event) => updateSubject(index, 'name', event.target.value)}
                    placeholder="Subject name"
                    className="w-full rounded border border-transparent px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </td>
                <td className="border-r border-t border-gray-200 px-2 py-1">
                  <input
                    value={subject.iat1Max}
                    onChange={(event) => updateSubject(index, 'iat1Max', event.target.value)}
                    className="mx-auto w-24 rounded border border-transparent px-2 py-1 text-center focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </td>
                <td className="border-r border-t border-gray-200 px-2 py-1">
                  <input
                    value={subject.iat2Max}
                    onChange={(event) => updateSubject(index, 'iat2Max', event.target.value)}
                    className="mx-auto w-24 rounded border border-transparent px-2 py-1 text-center focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </td>
                <td className="border-r border-t border-gray-200 px-2 py-1">
                  <input
                    value={subject.eseMax}
                    onChange={(event) => updateSubject(index, 'eseMax', event.target.value)}
                    className="mx-auto w-24 rounded border border-transparent px-2 py-1 text-center focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </td>
                <td className="border-r border-t border-gray-200 px-2 py-1">
                  <select
                    value={subject.teachingDivision || ''}
                    onChange={(event) => updateSubject(index, 'teachingDivision', event.target.value)}
                    className="w-full rounded border border-transparent px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">All divisions</option>
                    {divisions.map((division) => (
                      <option key={division} value={division}>{division}</option>
                    ))}
                  </select>
                </td>
                <td className="border-r border-t border-gray-200 px-2 py-1">
                  <select
                    value={subject.courseInchargeEmail || ''}
                    onChange={(event) => updateCourseIncharge(index, event.target.value)}
                    className="w-full rounded border border-transparent px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select faculty</option>
                    {faculty.map((item) => {
                      const key = getFacultyKey(item);
                      return (
                        <option key={key} value={key}>
                          {getFacultyName(item)}
                        </option>
                      );
                    })}
                  </select>
                </td>
                <td className="border-t border-gray-200 px-2 py-1 text-center">
                  <button
                    type="button"
                    onClick={() => removeSubject(index)}
                    className="inline-flex items-center rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {message && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{message}</div>}
    </div>
  );
};

export default SemesterSubjectsWidget;
